import { useState, useEffect, useCallback } from 'react';
import {
    importProductImages, getUnmatchedImages,
    matchUnmatchedImage, dismissUnmatchedImage, getProducts
} from '../lib/api';
import {
    Upload, CheckCircle, WarningCircle, Trash, MagnifyingGlass,
    ArrowRight, Image as ImageIcon, FileArrowUp, Info, XCircle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';

export default function ImageImport() {
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const [unmatched, setUnmatched] = useState([]);
    const [loadingUnmatched, setLoadingUnmatched] = useState(true);
    const [matchDialog, setMatchDialog] = useState(null);
    const [matchSearch, setMatchSearch] = useState('');
    const [matchResults, setMatchResults] = useState([]);
    const [matchLoading, setMatchLoading] = useState(false);

    const loadUnmatched = useCallback(async () => {
        try {
            const res = await getUnmatchedImages();
            setUnmatched(res.data.unmatched || []);
        } catch {
            /* ignore */
        } finally {
            setLoadingUnmatched(false);
        }
    }, []);

    useEffect(() => { loadUnmatched(); }, [loadUnmatched]);

    const handleImport = async () => {
        if (!file) { toast.error('Select a CSV file first'); return; }
        setImporting(true);
        setResult(null);
        try {
            const res = await importProductImages(file);
            setResult(res.data);
            toast.success(`Import complete: ${res.data.matched} matched, ${res.data.unmatched} unmatched`);
            setFile(null);
            loadUnmatched();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const handleDismiss = async (code) => {
        try {
            await dismissUnmatchedImage(code);
            setUnmatched(prev => prev.filter(u => u.product_code !== code));
            toast.success('Dismissed');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        }
    };

    const openMatchDialog = (row) => {
        setMatchDialog(row);
        setMatchSearch('');
        setMatchResults([]);
    };

    const searchProducts = async (q) => {
        if (!q || q.length < 2) { setMatchResults([]); return; }
        setMatchLoading(true);
        try {
            const res = await getProducts({ search: q, limit: 10 });
            setMatchResults(res.data.products || res.data || []);
        } catch {
            setMatchResults([]);
        } finally {
            setMatchLoading(false);
        }
    };

    const handleMatch = async (productKey) => {
        if (!matchDialog) return;
        try {
            const res = await matchUnmatchedImage(matchDialog.product_code, productKey);
            toast.success(`Matched! ${res.data.images_added} images added`);
            setUnmatched(prev => prev.filter(u => u.product_code !== matchDialog.product_code));
            setMatchDialog(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Match failed');
        }
    };

    return (
        <div className="space-y-6" data-testid="image-import-page">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Product Image Import</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Import product images from website CSV. Images are linked by URL — no manual re-upload required.
                </p>
            </div>

            {/* Upload Section */}
            <div className="bg-card border border-border rounded-lg p-5" data-testid="upload-section">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <FileArrowUp size={16} />
                    Upload Image CSV
                </h3>
                <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                            <Upload size={28} className="mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm font-medium">{file ? file.name : 'Click to select CSV file'}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Required columns: product_code, primary_image, image_urls
                            </p>
                        </div>
                        <input
                            type="file"
                            accept=".csv"
                            className="hidden"
                            onChange={e => setFile(e.target.files?.[0] || null)}
                            data-testid="csv-file-input"
                        />
                    </label>
                </div>
                <div className="flex items-center gap-3 mt-3">
                    <Button onClick={handleImport} disabled={!file || importing} data-testid="import-btn">
                        <Upload size={14} className="mr-1.5" />
                        {importing ? 'Importing...' : 'Import Images'}
                    </Button>
                    {file && (
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setResult(null); }}>
                            Clear
                        </Button>
                    )}
                </div>

                {/* Info box */}
                <div className="mt-4 p-3 bg-muted/30 rounded-md border border-border/50">
                    <div className="flex items-start gap-2">
                        <Info size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-muted-foreground space-y-1">
                            <p><strong>Matching:</strong> product_code is matched against internal Display SKU / product key. Slug is used as fallback.</p>
                            <p><strong>Duplicates:</strong> Rows with the same product_code are merged. Existing images are not duplicated.</p>
                            <p><strong>URLs:</strong> Image URLs are stored and rendered directly. No re-upload needed.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Import Result */}
            {result && (
                <div className="bg-card border border-border rounded-lg p-5" data-testid="import-result">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-600" />
                        Import Results
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-muted/30 rounded-md p-3 text-center">
                            <div className="text-2xl font-bold">{result.total_rows}</div>
                            <div className="text-[10px] text-muted-foreground uppercase">Total Products</div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-950/20 rounded-md p-3 text-center">
                            <div className="text-2xl font-bold text-green-700 dark:text-green-400">{result.matched}</div>
                            <div className="text-[10px] text-muted-foreground uppercase">Matched</div>
                        </div>
                        <div className={`rounded-md p-3 text-center ${result.unmatched > 0 ? 'bg-amber-50 dark:bg-amber-950/20' : 'bg-muted/30'}`}>
                            <div className={`text-2xl font-bold ${result.unmatched > 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}>{result.unmatched}</div>
                            <div className="text-[10px] text-muted-foreground uppercase">Unmatched</div>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-md p-3 text-center">
                            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{result.images_added}</div>
                            <div className="text-[10px] text-muted-foreground uppercase">Images Added</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Unmatched Queue */}
            <div className="bg-card border border-border rounded-lg" data-testid="unmatched-section">
                <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                        <WarningCircle size={16} className="text-amber-500" />
                        Unmatched Queue
                        {unmatched.length > 0 && (
                            <Badge variant="secondary" className="text-[10px]">{unmatched.length}</Badge>
                        )}
                    </h3>
                </div>

                {loadingUnmatched ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
                ) : unmatched.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-unmatched">
                        <CheckCircle size={24} className="mx-auto mb-2 text-green-500 opacity-60" />
                        No unmatched products. All image imports are resolved.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Product Code</TableHead>
                                    <TableHead>Slug</TableHead>
                                    <TableHead>Product Name</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Primary Image</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead className="w-28">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {unmatched.map((row, idx) => (
                                    <TableRow key={row.product_code + idx} data-testid={`unmatched-row-${idx}`}>
                                        <TableCell className="font-mono text-xs font-semibold">{row.product_code}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{row.slug || '\u2014'}</TableCell>
                                        <TableCell className="text-xs max-w-[200px] truncate">{row.product_name}</TableCell>
                                        <TableCell><Badge variant="outline" className="text-[10px]">{row.category || '\u2014'}</Badge></TableCell>
                                        <TableCell>
                                            {row.primary_image ? (
                                                <img src={row.primary_image} alt="" className="w-8 h-8 rounded object-cover border border-border" onError={e => { e.target.style.display = 'none'; }} />
                                            ) : (
                                                <span className="text-xs text-muted-foreground">\u2014</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs text-amber-600 max-w-[180px] truncate">{row.reason}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => openMatchDialog(row)} data-testid={`match-btn-${idx}`}>
                                                    <ArrowRight size={12} className="mr-0.5" />Match
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDismiss(row.product_code)} data-testid={`dismiss-btn-${idx}`}>
                                                    <XCircle size={14} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Match Dialog */}
            <Dialog open={!!matchDialog} onOpenChange={(open) => !open && setMatchDialog(null)}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Match to Internal Product</DialogTitle>
                        <DialogDescription>
                            Search for an internal product to link <strong>{matchDialog?.product_code}</strong> ({matchDialog?.product_name})
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {matchDialog?.primary_image && (
                            <div className="flex items-center gap-3 p-2 bg-muted/30 rounded">
                                <img src={matchDialog.primary_image} alt="" className="w-12 h-12 rounded object-cover border border-border" />
                                <div>
                                    <div className="text-xs font-mono font-semibold">{matchDialog.product_code}</div>
                                    <div className="text-xs text-muted-foreground">{matchDialog.product_name}</div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <MagnifyingGlass size={14} className="text-muted-foreground" />
                            <Input
                                value={matchSearch}
                                onChange={e => {
                                    setMatchSearch(e.target.value);
                                    searchProducts(e.target.value);
                                }}
                                placeholder="Search by SKU or product name..."
                                className="text-sm"
                                data-testid="match-search-input"
                            />
                        </div>
                        {matchLoading && <p className="text-xs text-muted-foreground">Searching...</p>}
                        {matchResults.length > 0 && (
                            <div className="space-y-1 max-h-60 overflow-y-auto">
                                {matchResults.map(p => (
                                    <button
                                        key={p.product_key}
                                        className="w-full text-left px-3 py-2 text-xs rounded hover:bg-muted/50 transition-colors border border-border/30"
                                        onClick={() => handleMatch(p.product_key)}
                                        data-testid={`match-option-${p.product_key}`}
                                    >
                                        <span className="font-mono font-semibold">{p.display_sku}</span>
                                        <span className="text-muted-foreground ml-2">{p.product_name}</span>
                                        <span className="text-muted-foreground ml-2 text-[10px]">{p.product_key}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
